
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsBuilding } from "react-icons/bs";
import { LuUsers } from "react-icons/lu";
import { IoAddSharp } from "react-icons/io5";
import Header from "../component/Header";
import { FiDollarSign, FiTrash2, FiSearch, FiX, FiChevronDown } from "react-icons/fi";
import { API_BASE_URL } from "../utils/apiConfig";
import { FaRupeeSign } from "react-icons/fa";
import Swal from 'sweetalert2';
function UserPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [todayRevenue, setTodayRevenue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expandedEmails, setExpandedEmails] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const userRole = localStorage.getItem("role");

    const toggleEmailExpand = (email) => {
        setExpandedEmails(prev => ({
            ...prev,
            [email]: !prev[email]
        }));
    };
    const currentUserId = localStorage.getItem("user_id_PK");

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/");
                    return;
                }
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const usersRes = await axios.get(`${API_BASE_URL}/api/admins`, config);
                setUsers(usersRes.data);

                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);
            } catch (err) {
                console.error("Failed to fetch data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [navigate]);

    useEffect(() => {
        const fetchRevenue = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const config = { headers: { Authorization: `Bearer ${token}` } };

                let url = `${API_BASE_URL}/api/orders/today-revenue`;
                if (selectedDeviceId) {
                    url += `?device_id=${selectedDeviceId}`;
                }
                const revenueRes = await axios.get(url, config);
                if (revenueRes.data.status) {
                    setTodayRevenue(revenueRes.data.todayRevenue);
                }
            } catch (err) {
                console.error("Failed to fetch revenue", err);
            }
        };
        fetchRevenue();
    }, [selectedDeviceId]);

    const deleteUser = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to remove this user? This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete user!",
            background: '#1b1d1a',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("token");
                    await axios.delete(`${API_BASE_URL}/api/admins/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUsers(users.filter(u => u.user_id_PK !== id));
                    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'User has been removed.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Error', text: "Failed to delete user", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
                }
            }
        });
    };

    const currentUser = users.find(u => String(u.user_id_PK) === String(currentUserId));
    const currentUserEmail = currentUser ? currentUser.email : null;

    const filteredUsers = userRole === 'superadmin'
        ? users
        : users.filter(u => u.email === currentUserEmail);

    let displayedUsers = selectedDeviceId
        ? filteredUsers.filter(u => String(u.device_id) === String(selectedDeviceId))
        : filteredUsers;

    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        displayedUsers = displayedUsers.filter(u =>
            (u.name?.toLowerCase().includes(query)) ||
            (u.email?.toLowerCase().includes(query)) ||
            (u.organization_name?.toLowerCase().includes(query)) ||
            (u.device_name?.toLowerCase().includes(query)) ||
            (u.location?.toLowerCase().includes(query))
        );
    }

    const groupedUsers = Object.values(displayedUsers.reduce((acc, user) => {
        if (!acc[user.email]) {
            acc[user.email] = [];
        }
        acc[user.email].push(user);
        return acc;
    }, {}));

    const adminCount = displayedUsers.filter(u => u.role === 'admin').length;
    const techCount = displayedUsers.filter(u => u.role === 'technician').length;

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Loading User Accounts...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="mt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-xl font-bold text-white">Dashboard Overview</h2>
                    <p className="text-gray-500 text-xs mt-1">Key metrics across operations</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 ">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <BsBuilding className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Total Device Partners</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">{adminCount}</h2>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <LuUsers className="text-blue-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Active Technicians</p>
                            <h2 className="text-blue-400 text-3xl font-bold mt-1">{techCount}</h2>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition">
                    <div className="flex items-start gap-3">
                        <FaRupeeSign className="text-green-400 text-3xl mt-3" />
                        <div>
                            <p className="text-gray-400 text-sm">Today's Revenue</p>
                            <h2 className="text-green-400 text-3xl font-bold mt-1">₹{todayRevenue.toLocaleString()}</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <h2 className="text-xl font-bold text-green-400">User List</h2>

                        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                            {/* Device Selector */}
                            <div className="relative flex items-center h-9 bg-black/40 rounded-xl border border-white/5 pl-3 pr-2 transition focus-within:ring-1 focus-within:ring-green-500">
                                <select
                                    value={selectedDeviceId}
                                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                                    className="bg-transparent text-gray-300 text-[11px] font-bold outline-none cursor-pointer appearance-none pr-6 w-28 md:w-36 truncate"
                                >
                                    <option value="" className="bg-[#1b1d1a] text-white">All Devices</option>
                                    {devices.map(device => (
                                        <option key={device.device_id_PK} value={device.device_id_PK} className="bg-[#1b1d1a] text-white">
                                            {device.device_name}
                                        </option>
                                    ))}
                                </select>
                                <FiChevronDown size={11} className="absolute right-3 text-gray-600 pointer-events-none" />
                            </div>

                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-green-500 transition w-48 md:w-64 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            {userRole === 'superadmin' && (
                                <button
                                    onClick={() => navigate("/addUser")}
                                    className="flex items-center gap-2 px-4 h-9 text-sm bg-green-400 font-semibold rounded-xl text-black hover:bg-green-500 transition shadow-lg shadow-green-900/20 active:scale-95 shrink-0"
                                >
                                    <IoAddSharp size={16} /> Add New Account
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-gray-300">
                            <thead>
                                <tr className="border-b border-gray-700 text-gray-500 uppercase tracking-wider text-[11px]">
                                    <th className="px-4 py-3 text-left">Organization</th>
                                    <th className="px-4 py-3 text-left">Device Name</th>
                                    <th className="px-4 py-3 text-left">User Name</th>
                                    <th className="px-4 py-3 text-left">Email</th>
                                    <th className="px-4 py-3 text-left">Location</th>
                                    <th className="px-4 py-3 text-left">Role</th>
                                    <th className="px-4 py-3 text-left">Linked Device ID</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {groupedUsers.map((group) => {
                                    const primaryUser = group[0];
                                    const isMultiple = group.length > 1;
                                    const isExpanded = expandedEmails[primaryUser.email];

                                    return (
                                        <React.Fragment key={primaryUser.email}>
                                            <tr
                                                className={`border-b border-gray-800 hover:bg-[#1e1e1e] transition ${isMultiple ? 'cursor-pointer' : ''}`}
                                                onClick={() => isMultiple && toggleEmailExpand(primaryUser.email)}
                                            >
                                                <td className="px-4 py-3">
                                                    <p className="text-white font-semibold">{primaryUser.organization_name}</p>
                                                </td>
                                                <td className="px-4 py-3 text-gray-400">
                                                    {isMultiple ? (
                                                        <span className="text-green-400 font-semibold">{group.length} Devices (Click to {isExpanded ? 'Collapse' : 'Expand'})</span>
                                                    ) : (
                                                        primaryUser.device_name
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.name}</td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.email}</td>
                                                <td className="px-4 py-3 text-gray-400">{primaryUser.location}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${primaryUser.role === 'admin' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                                                        {primaryUser.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isMultiple ? (
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-green-400">
                                                            {isExpanded ? '▼ Collapse' : '▶ Expand'}
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-gray-400">
                                                            ID: {primaryUser.device_id || 'unassigned'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    {!isMultiple && userRole === 'superadmin' && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); deleteUser(primaryUser.user_id_PK); }}
                                                            className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {isMultiple && isExpanded && group.map(user => (
                                                <tr key={user.user_id_PK} className="border-b border-gray-800 bg-[#161815] hover:bg-[#1e1e1e] transition">
                                                    <td className="px-4 py-3 pl-8 border-l-2 border-green-500">
                                                        <p className="text-gray-400 text-xs">↳ {user.organization_name}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-400">{user.device_name}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.name}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.email}</td>
                                                    <td className="px-4 py-3 text-gray-500">{user.location}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${user.role === 'admin' ? 'border-green-400 text-green-400' : 'border-blue-400 text-blue-400'}`}>
                                                            {user.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-3 py-1 text-[11px] font-semibold rounded-full bg-gray-900 border border-gray-800 text-gray-400">
                                                            ID: {user.device_id || 'unassigned'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {userRole === 'superadmin' && (
                                                            <button
                                                                onClick={() => deleteUser(user.user_id_PK)}
                                                                className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition"
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserPage;
